import {
  API_ERROR,
  HTTP_STATUS,
  IDEMPOTENCY_KEY_HEADER,
  IF_MATCH_HEADER,
} from '../../shared/constants.ts';
import { nodeSegment } from '../../shared/navigation.ts';
import { idSchema, nodeTypeSchema, pathEntrySchema } from '../../shared/schemas/common.ts';
import {
  OFFLINE_RETRY_BASE_DELAY_MS,
  OFFLINE_RETRY_MAX_ATTEMPTS,
  OFFLINE_RETRY_MAX_DELAY_MS,
} from './constants.ts';
import { countQueued, offlineState, setOfflineState } from './state.ts';
import {
  dequeue,
  dropEntitiesUnder,
  enqueue,
  getEntity,
  listQueue,
  OfflineParentError,
  putEntity,
  updateKeptPaths,
  updateQueued,
} from './store.ts';
import type { createdNodeDetail } from './bundle.ts';
import type { QueuedFile, QueuedWrite } from './db.ts';

// The write queue: what happens to an edit made with no signal, and what
// happens to it when the signal comes back.
//
// A queued write carries everything needed to send it later without the app
// having to remember anything: its method and path, its body (a JSON value,
// or the text and file parts of an upload kept as bytes), the
// `Idempotency-Key` that makes replaying it safe, and the `If-Match` of the
// row it was made against. They replay oldest first, so an item added to a
// room that was itself made offline finds its room already there — the room
// carries the id the client generated, and the item's path names it.
//
// Sending is done with `fetch` directly rather than through `apiRequest`: a
// send that fails must go back in the queue, not be queued a second time.

/** An id in the shape the Worker generates, for a node made with no signal. */
export function newClientId(): string {
  return crypto.randomUUID();
}

/**
 * The store key of a read or a node: its path under `/api`, without the
 * query string. `/api/rooms/abc` is `rooms/abc`, which is the node's type
 * and id; `/api/rooms/abc/notes` is that node's notes.
 */
export function entityKeyFor(path: string): string {
  const [withoutQuery = ''] = path.split('?');
  return withoutQuery.replace(/^\/api\//u, '');
}

/**
 * What a write does to the copy the pages read while there is no signal.
 *
 * The caller says only what it knows: which node, what the write does, the
 * key the row sits under in both the reply and the node's own read (`room`,
 * `item`, `note`), and the fields it is writing. The rest — the row a
 * create becomes, the row an update leaves behind — is built from the kept
 * copy here, because the copy is the only thing that knows what the row
 * looked like before.
 */
export interface OfflineWrite {
  readonly effect: 'create' | 'update' | 'delete';
  /** The node the write is about, as a store key (`rooms/<id>`). */
  readonly entity: string;
  /** The key the row sits under in the reply and in the node's own read. */
  readonly holder: string;
  /** A create's whole row, or an update's changed fields. */
  readonly row: Readonly<Record<string, unknown>>;
  /** Fields the reply carries beside the row, e.g. an item edit's `brokenLinks`. */
  readonly extra?: Readonly<Record<string, unknown>>;
  /** The cached list the created row joins, or the deleted row leaves. */
  readonly list?: { readonly entity: string; readonly key: string };
}

/**
 * What one request that stands for several writes means with no signal.
 *
 * `apiRequest` calls it only when the server could not be reached. It queues
 * the writes the single request would have made — the runs a pattern makes
 * become one create each — and answers the reply the caller's parser will
 * validate. Throwing instead refuses the whole thing with nothing queued,
 * which is how a check the route makes before it writes anything (a label
 * already in the room) is made here first.
 */
export type OfflineFanOut = () => Promise<unknown>;

/**
 * The reply a queued write answers with, and the row its node's copy now
 * holds. A create is the row as given; an update is the row the copy already
 * held with the changed fields over it and a fresh stamp, so the page shows
 * the edit and the next write quotes the version it was made against; a
 * delete answers nothing, as the route does.
 */
async function replyFor(write: OfflineWrite): Promise<{
  readonly reply: unknown;
  readonly row: Record<string, unknown> | null;
  readonly version: string | null;
}> {
  const kept = await getEntity(write.entity);
  let held: unknown =
    kept === null || typeof kept.body !== 'object' || kept.body === null
      ? undefined
      : (kept.body as Record<string, unknown>)[write.holder];
  const { list } = write;
  if ((typeof held !== 'object' || held === null) && list !== undefined) {
    const parent = await getEntity(list.entity);
    const body = parent?.body;
    const rows: unknown =
      typeof body === 'object' && body !== null
        ? Object.entries(body).find(([key]) => key === list.key)?.[1]
        : undefined;
    const id = write.entity.split('/').at(-1);
    if (Array.isArray(rows)) {
      held = rows.find(
        (entry: unknown) =>
          typeof entry === 'object' && entry !== null && 'id' in entry && entry.id === id,
      );
    }
  }
  const before = typeof held === 'object' && held !== null ? (held as Record<string, unknown>) : {};
  const version =
    kept?.updatedAt ?? (typeof before['updatedAt'] === 'string' ? before['updatedAt'] : null);
  if (write.effect === 'delete') {
    return { reply: undefined, row: null, version };
  }
  const row =
    write.effect === 'create'
      ? { ...write.row }
      : { ...before, ...write.row, updatedAt: new Date().toISOString() };
  return { reply: { [write.holder]: row, ...write.extra }, row, version };
}

/** The parts of a multipart body, split into text and files for storage. */
export function splitForm(form: FormData): {
  readonly text: Record<string, string>;
  readonly files: QueuedFile[];
} {
  const text: Record<string, string> = {};
  const files: QueuedFile[] = [];
  for (const [field, value] of form.entries()) {
    if (typeof value === 'string') {
      text[field] = value;
    } else {
      files.push({ field, blob: value, filename: value.name });
    }
  }
  return { text, files };
}

/** Rebuild the body a queued upload was made with. */
function formOf(write: QueuedWrite): FormData {
  const form = new FormData();
  for (const [field, value] of Object.entries(write.form ?? {})) {
    form.set(field, value);
  }
  for (const file of write.files) {
    form.set(file.field, file.blob, file.filename);
  }
  return form;
}

/**
 * Show the edit in the kept copy at once, so the page the person is looking
 * at reflects what they just did even though nothing has been sent. An
 * update puts the changed row back under its own key; a create files the new
 * row and appends it to the list it belongs to; a delete removes the node,
 * everything kept beneath it, and its line in its parent's list.
 */
async function applyOptimistically(
  write: OfflineWrite,
  row: Record<string, unknown> | null,
  createdBody?: Record<string, unknown>,
): Promise<void> {
  const now = new Date().toISOString();
  if (write.effect === 'delete') {
    await dropEntitiesUnder(write.entity);
  } else if (row !== null) {
    const existing = await getEntity(write.entity);
    const body =
      existing === null || typeof existing.body !== 'object' || existing.body === null
        ? {}
        : (existing.body as Record<string, unknown>);
    await putEntity({
      key: write.entity,
      body: { ...body, ...createdBody, [write.holder]: row },
      updatedAt:
        typeof row['updatedAt'] === 'string' ? row['updatedAt'] : (existing?.updatedAt ?? null),
      keptAt: now,
    });
    const entry = pathEntrySchema.safeParse({
      type: write.holder,
      id: row['id'],
      name: row['name'] ?? row['label'],
      identifier: row['identifier'],
    });
    if (entry.success) {
      await updateKeptPaths(entry.data);
    }
  }
  const { list } = write;
  if (list === undefined) {
    return;
  }
  const parent = await getEntity(list.entity);
  if (parent === null || typeof parent.body !== 'object' || parent.body === null) {
    return;
  }
  const body = parent.body as Record<string, unknown>;
  const current = Array.isArray(body[list.key]) ? (body[list.key] as unknown[]) : [];
  const id = write.entity.split('/').at(-1);
  const next =
    write.effect === 'create'
      ? [...current, row ?? {}]
      : write.effect === 'update'
        ? current.map((entry) =>
            typeof entry === 'object' && entry !== null && 'id' in entry && entry.id === id
              ? row
              : entry,
          )
        : current.filter((entry) => (entry as { id?: unknown }).id !== id);
  await putEntity({
    key: list.entity,
    body: { ...body, [list.key]: next },
    updatedAt: parent.updatedAt,
    keptAt: now,
  });
}

/**
 * Put a write in the queue, show its effect at once, and answer with the
 * reply the caller's parser will validate.
 */
export async function queueWrite(
  request: {
    readonly method: string;
    readonly path: string;
    readonly body: unknown;
    readonly form: FormData | undefined;
  },
  write: OfflineWrite,
): Promise<unknown> {
  const parts = request.form === undefined ? null : splitForm(request.form);
  const { reply, row, version } = await replyFor(write);
  const type = nodeTypeSchema.safeParse(write.holder);
  const id = idSchema.safeParse(row?.['id']);
  // The queue also carries non-node records and transport-only partial rows.
  // Canonical nodes have UUIDs and must be openable from a complete kept detail.
  let created: Awaited<ReturnType<typeof createdNodeDetail>> | undefined;
  if (write.effect === 'create' && row !== null && type.success && id.success) {
    if (write.entity !== `${nodeSegment(type.data)}/${id.data}`) {
      throw new OfflineParentError(
        'The new node identity does not match its cached address. Nothing was queued.',
      );
    }
    const { createdNodeDetail: buildDetail } = await import('./bundle.ts');
    created = await buildDetail(type.data, row, write.list?.entity);
  }
  await enqueue({
    method: request.method,
    path: request.path,
    body: request.body ?? null,
    form: parts?.text ?? null,
    files: parts?.files ?? [],
    idempotencyKey: newClientId(),
    ifMatch: version,
    entity: write.entity,
    createdIds: write.effect === 'create' ? [write.entity.split('/').at(-1) ?? ''] : [],
    createdAt: new Date().toISOString(),
    attempts: 0,
    nextAttemptAt: '',
    lastError: '',
    status: 'queued',
    serverCurrent: null,
    mine: created?.row ?? write.row,
  });
  await applyOptimistically(write, created?.row ?? row, created?.body);
  if (created !== undefined) {
    for (const read of created.related) {
      await putEntity({ ...read, keptAt: new Date().toISOString() });
    }
  }
  countQueued();
  return created === undefined ? reply : { [write.holder]: created.row, ...write.extra };
}

/**
 * The file parts of the upload still waiting to be sent for `entity`, or
 * null when nothing is waiting for it. A photo taken with no signal has its
 * bytes here and nowhere else until the upload lands, so the gallery reads
 * the picture it is showing from them rather than from a server route that
 * has never heard of the photo.
 */
export async function queuedUpload(entity: string): Promise<readonly QueuedFile[] | null> {
  const queue = await listQueue();
  const upload = queue.find((write) => write.entity === entity && write.files.length > 0);
  return upload?.files ?? null;
}

/** The wait before attempt number `attempts` (counting from 1): doubling, capped. */
export function backoffMs(attempts: number): number {
  return Math.min(OFFLINE_RETRY_BASE_DELAY_MS * 2 ** (attempts - 1), OFFLINE_RETRY_MAX_DELAY_MS);
}

/** Whether a queued write is due to be attempted now. */
function due(write: QueuedWrite, now: number): boolean {
  if (write.status !== 'queued') {
    return false;
  }
  return write.nextAttemptAt === '' || Date.parse(write.nextAttemptAt) <= now;
}

interface Refusal {
  readonly status: number;
  readonly code: string;
  readonly message: string;
  readonly current: unknown;
}

async function refusalFrom(response: Response): Promise<Refusal> {
  let wire: Record<string, unknown> = {};
  try {
    wire = (await response.json()) as Record<string, unknown>;
  } catch {
    // A reply that is not JSON still tells us its status, which is what
    // decides whether the write waits or stops.
  }
  return {
    status: response.status,
    code: typeof wire['error'] === 'string' ? wire['error'] : '',
    message:
      typeof wire['message'] === 'string'
        ? wire['message']
        : `The server answered ${String(response.status)}`,
    current: wire['current'] ?? null,
  };
}

/** Send one queued write. Answers what became of it. */
async function send(write: QueuedWrite): Promise<QueuedWrite | null> {
  const headers = new Headers({
    Accept: 'application/json',
    [IDEMPOTENCY_KEY_HEADER]: write.idempotencyKey,
  });
  if (write.ifMatch !== null) {
    headers.set(IF_MATCH_HEADER, `"${write.ifMatch}"`);
  }
  const init: RequestInit = { method: write.method, credentials: 'same-origin', headers };
  if (write.form !== null) {
    init.body = formOf(write);
  } else if (write.body !== null) {
    headers.set('Content-Type', 'application/json');
    init.body = JSON.stringify(write.body);
  }
  const response = await fetch(write.path, init);
  if (response.ok) {
    return null;
  }
  const refusal = await refusalFrom(response);
  if (refusal.status === HTTP_STATUS.PRECONDITION_FAILED && refusal.code === API_ERROR.STALE) {
    return {
      ...write,
      status: 'conflict',
      serverCurrent: refusal.current,
      lastError: refusal.message,
    };
  }
  if (
    refusal.status === HTTP_STATUS.TOO_MANY_REQUESTS ||
    refusal.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR
  ) {
    return laterOrFailed(write, refusal.message);
  }
  return { ...write, status: 'failed', lastError: refusal.message };
}

/** A write that could not be sent waits, or gives up once it has tried enough. */
function laterOrFailed(write: QueuedWrite, reason: string): QueuedWrite {
  const attempts = write.attempts + 1;
  if (attempts >= OFFLINE_RETRY_MAX_ATTEMPTS) {
    return { ...write, attempts, status: 'failed', lastError: reason };
  }
  return {
    ...write,
    attempts,
    status: 'queued',
    nextAttemptAt: new Date(Date.now() + backoffMs(attempts)).toISOString(),
    lastError: reason,
  };
}

let draining = false;

/**
 * What the writes still waiting ahead of a given point stand for: the ids
 * their creates will produce, and the entities they touch. A write behind
 * them that names either — an item in a room made offline, a second edit
 * of the same row — depends on them landing first.
 */
interface Waiting {
  readonly ids: Set<string>;
  readonly entities: Set<string>;
}

function holdBehind(waiting: Waiting, write: QueuedWrite): void {
  for (const id of write.createdIds) {
    waiting.ids.add(id);
  }
  // The row an update or delete is about, so a write under it waits too.
  const own = write.entity.split('/').at(-1);
  if (own !== undefined && own !== '') {
    waiting.ids.add(own);
  }
  waiting.entities.add(write.entity);
}

/** Whether `write` must wait for something still ahead of it in the queue. */
function dependsOnWaiting(write: QueuedWrite, waiting: Waiting): boolean {
  if (waiting.entities.has(write.entity)) {
    return true;
  }
  for (const id of waiting.ids) {
    if (write.path.includes(id) || write.entity.includes(id)) {
      return true;
    }
  }
  return false;
}

/**
 * Send everything due, oldest first, keeping order where order matters: a
 * write behind one that is still waiting — failed, refused as a conflict,
 * backing off, or just refused now — is sent only when it depends on none
 * of them (a note on one room while a rename of another room waits), and
 * holds otherwise (an item in a room whose create has not landed, a second
 * edit of a row whose first is waiting). A send the browser could not make
 * at all ends the round: the connection is gone, and trying the rest would
 * only spend their attempts on the same silence. Answers how many landed.
 */
export async function drainQueue(): Promise<number> {
  if (draining) {
    return 0;
  }
  draining = true;
  let landed = 0;
  try {
    const now = Date.now();
    const waiting: Waiting = { ids: new Set(), entities: new Set() };
    for (const write of await listQueue()) {
      if (!due(write, now) || dependsOnWaiting(write, waiting)) {
        holdBehind(waiting, write);
        continue;
      }
      await updateQueued({ ...write, status: 'sending' });
      let outcome: QueuedWrite | null;
      let unreachable = false;
      try {
        outcome = await send(write);
      } catch (error) {
        unreachable = true;
        outcome = laterOrFailed(
          write,
          error instanceof Error ? error.message : 'The server could not be reached',
        );
      }
      if (outcome === null) {
        await dequeue(write.id ?? 0);
        landed += 1;
        continue;
      }
      await updateQueued(outcome);
      if (unreachable) {
        break;
      }
      holdBehind(waiting, write);
    }
  } finally {
    draining = false;
  }
  await refreshQueueCounts();
  return landed;
}

/** Republish the counts the banner and the Offline page read. */
export async function refreshQueueCounts(): Promise<void> {
  const queue = await listQueue();
  const failed = queue.filter((write) => write.status === 'failed').length;
  const conflicts = queue.filter((write) => write.status === 'conflict').length;
  setOfflineState({
    queued: queue.length - failed - conflicts,
    failed,
    conflicts,
    syncedAt: queue.length === 0 ? new Date().toISOString() : offlineState().syncedAt,
  });
}

/** One queued write by the number the store gave it, or undefined. */
async function queuedWrite(id: number): Promise<QueuedWrite | undefined> {
  const queue = await listQueue();
  return queue.find((entry) => entry.id === id);
}

/** Put a failed write back in the queue for another try, now. */
export async function retryQueued(id: number): Promise<void> {
  const write = await queuedWrite(id);
  if (write === undefined) {
    return;
  }
  await updateQueued({ ...write, status: 'queued', attempts: 0, nextAttemptAt: '', lastError: '' });
  await drainQueue();
}

/**
 * Throw a queued write away. The node's kept copy goes with it, because the
 * copy is showing an edit that will now never be sent; the next read of it
 * fetches the server's own.
 */
export async function discardQueued(id: number): Promise<void> {
  const write = await queuedWrite(id);
  if (write === undefined) {
    return;
  }
  await dequeue(id);
  await dropEntitiesUnder(write.entity);
  await refreshQueueCounts();
}

/**
 * "Keep mine": send the write again against the version the server has now,
 * so the edit lands on top of what changed underneath it. A fresh
 * idempotency key goes with it, because this is a new decision rather than a
 * replay of the old one.
 */
export async function keepMine(id: number): Promise<void> {
  const write = await queuedWrite(id);
  if (write === undefined) {
    return;
  }
  const current = write.serverCurrent as { updatedAt?: unknown } | null;
  const ifMatch = typeof current?.updatedAt === 'string' ? current.updatedAt : null;
  await updateQueued({
    ...write,
    ifMatch,
    idempotencyKey: newClientId(),
    status: 'queued',
    attempts: 0,
    nextAttemptAt: '',
    lastError: '',
    serverCurrent: null,
  });
  await drainQueue();
}

/** "Take theirs": drop the write and let the server's copy stand. */
export async function takeTheirs(id: number): Promise<void> {
  await discardQueued(id);
}
